import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useContext
} from "react";
import {
  Box,
  Button,
  Modal,
  IconButton,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
  Paper
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { LoadingButton } from "@mui/lab";
import { useMediaQuery } from "@mui/material";
import { BRAND_COLOR, BRAND_HOVER } from "@kivunova/kivufrontendcommon";
import { CompanyStore, UpdateStoreRequest } from "./types/storeType";
import { useUpdateStore } from "../../hooks/api/useStore";
import { useDispatch } from "react-redux";
import { updateStore } from "../../store/slices/storeSlice";
import {
  useRwandaLocation,
  useKivunovaTranslation,
  CountryFullName,
  PhoneCountryCodeMap,
  splitPhoneNumber,
  combinePhoneNumber,
  isValidPhoneNumberForCountry,
  AlertBannerContext
} from "@kivunova/kivufrontendcommon";
import {
  ALERT_BANNER_CODE,
  ALERT_BANNER_CODE_SEVERITY,
  API_RESPONSE_CODE
} from "@kivunova/kivufrontendcommon";
import { StoreStatus } from "./enums/storeEnums";
import { CountryCode } from "libphonenumber-js";

interface UpdateStoreModalProps {
  open: boolean;
  onClose: () => void;
  store: CompanyStore;
}

const UpdateStoreModal: React.FC<UpdateStoreModalProps> = ({
  open,
  onClose,
  store
}) => {
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useDispatch();
  const { t } = useKivunovaTranslation();
  const { setAlert } = useContext(AlertBannerContext);

  const updateStoreMutation = useUpdateStore();

  const {
    getProvinces,
    getDistrictByProvince,
    getSectors,
    getCells,
    getVillages,
    loading: locationLoading
  } = useRwandaLocation();

  const initialPhoneSplit = splitPhoneNumber(
    store.contactPhone || "",
    PhoneCountryCodeMap
  );

  const [name, setName] = useState(store.name);
  const [status, setStatus] = useState(store.status || StoreStatus.ACTIVE);
  const [email, setEmail] = useState(store.contactEmail || "");
  const [phoneNumber, setPhoneNumber] = useState(initialPhoneSplit.number);
  const [phoneCountryCode, setPhoneCountryCode] = useState(
    initialPhoneSplit.code
  );
  const [phoneError, setPhoneError] = useState("");
  const [country, setCountry] = useState(
    store.address?.country || CountryFullName.RW
  );
  const [province, setProvince] = useState(store.address?.province || "");
  const [district, setDistrict] = useState(store.address?.district || "");
  const [sector, setSector] = useState(store.address?.sector || "");
  const [cell, setCell] = useState(store.address?.cell || "");
  const [village, setVillage] = useState(store.address?.village || "");
  const [streetAddress, setStreetAddress] = useState(
    store.address?.streetAddress || ""
  );

  useEffect(() => {
    if (store) {
      const phoneSplit = splitPhoneNumber(
        store.contactPhone || "",
        PhoneCountryCodeMap
      );
      setName(store.name);
      setStatus(store.status || StoreStatus.ACTIVE);
      setEmail(store.contactEmail || "");
      setPhoneNumber(phoneSplit.number);
      setPhoneCountryCode(phoneSplit.code);
      setCountry(store.address?.country || CountryFullName.RW);
      setProvince(store.address?.province || "");
      setDistrict(store.address?.district || "");
      setSector(store.address?.sector || "");
      setCell(store.address?.cell || "");
      setVillage(store.address?.village || "");
      setStreetAddress(store.address?.streetAddress || "");
    }
  }, [store]);

  const provinces = useMemo(() => getProvinces(), [getProvinces]);
  const districts = useMemo(
    () => (province ? getDistrictByProvince(province) : []),
    [province, getDistrictByProvince]
  );
  const sectors = useMemo(
    () => (province && district ? getSectors(province, district) : []),
    [province, district, getSectors]
  );
  const cells = useMemo(
    () =>
      province && district && sector
        ? getCells(province, district, sector)
        : [],
    [province, district, sector, getCells]
  );
  const villages = useMemo(
    () =>
      province && district && sector && cell
        ? getVillages(province, district, sector, cell)
        : [],
    [province, district, sector, cell, getVillages]
  );

  const handleProvinceChange = useCallback((val: string) => {
    setProvince(val);
    setDistrict("");
    setSector("");
    setCell("");
    setVillage("");
  }, []);
  const handleDistrictChange = useCallback((val: string) => {
    setDistrict(val);
    setSector("");
    setCell("");
    setVillage("");
  }, []);
  const handleSectorChange = useCallback((val: string) => {
    setSector(val);
    setCell("");
    setVillage("");
  }, []);
  const handleCellChange = useCallback((val: string) => {
    setCell(val);
    setVillage("");
  }, []);

  const validatePhone = useCallback(
    (number: string, countryCode: string) => {
      if (!number || !countryCode) {
        setPhoneError(t("phone-required", "Phone number is required"));
        return false;
      }

      const countryCodeValue =
        PhoneCountryCodeMap[countryCode]?.code || countryCode;

      const countryKey = Object.keys(PhoneCountryCodeMap).find(
        (key) => PhoneCountryCodeMap[key].code === countryCodeValue
      );

      if (!countryKey) {
        setPhoneError(t("invalid-country-code", "Invalid country code"));
        return false;
      }

      const countryCodeForValidation = countryKey as CountryCode;

      const isValid = isValidPhoneNumberForCountry(
        number,
        countryCodeForValidation,
        countryCodeValue
      );

      if (!isValid) {
        setPhoneError(
          t("invalid-phone", "Invalid phone number for selected country")
        );
        return false;
      }

      setPhoneError("");
      return true;
    },
    [t]
  );

  const handlePhoneNumberChange = (value: string) => {
    setPhoneNumber(value);
    if (value) {
      validatePhone(value, phoneCountryCode);
    } else {
      setPhoneError("");
    }
  };

  const handlePhoneCountryCodeChange = (value: string) => {
    setPhoneCountryCode(value);
    if (phoneNumber) {
      validatePhone(phoneNumber, value);
    }
  };

  const handleUpdate = async () => {
    if (updateStoreMutation.isPending) return;

    // Validate phone before submitting
    if (phoneNumber && !validatePhone(phoneNumber, phoneCountryCode)) {
      return;
    }

    const fullPhoneNumber = combinePhoneNumber(
      phoneCountryCode,
      phoneNumber,
      PhoneCountryCodeMap
    );

    const payload: UpdateStoreRequest = {
      storeId: store.id,
      updates: {
        name,
        status: status as StoreStatus,
        contactEmail: email,
        contactPhone: fullPhoneNumber,
        address: {
          country,
          province,
          district,
          sector,
          cell,
          village,
          streetAddress
        }
      }
    };

    try {
      await updateStoreMutation.mutateAsync(payload, {
        onSuccess: (response) => {
          const { code, store: updatedStore } = response.updateStore;
          if (code === API_RESPONSE_CODE.OK && updatedStore) {
            dispatch(updateStore(updatedStore as CompanyStore));
            setAlert({
              severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS,
              code: ALERT_BANNER_CODE.STORE_UPDATED
            });
            onClose();
          } else {
            setAlert({
              severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
              code: code as ALERT_BANNER_CODE
            });
          }
        },
        onError: (error) => {
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
            code: error?.message
              ? (error.message as ALERT_BANNER_CODE)
              : ALERT_BANNER_CODE.INTERNAL_ERROR
          });
        }
      });
    } catch {
      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
        code: ALERT_BANNER_CODE.INTERNAL_ERROR
      });
    }
  };

  const currentFullPhone = combinePhoneNumber(
    phoneCountryCode,
    phoneNumber,
    PhoneCountryCodeMap
  );

  const isFormDirty =
    name !== store.name ||
    status !== store.status ||
    email !== (store.contactEmail || "") ||
    currentFullPhone !== (store.contactPhone || "") ||
    province !== store.address?.province ||
    district !== store.address?.district ||
    sector !== store.address?.sector ||
    cell !== store.address?.cell ||
    village !== store.address?.village ||
    streetAddress !== store.address?.streetAddress;

  const isLoading = updateStoreMutation.isPending;

  return (
    <Modal open={open} onClose={onClose}>
      <Box className="modal-styles">
        {/* ── modal-header: fixed top — title + close button ── */}
        <Box className="modal-header">
          <Typography
            variant={matchMD ? "h6" : "h5"}
            fontWeight="bold"
            color="textPrimary"
          >
            Update Store
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* ── modal-body: the only scrolling region ── */}
        <Box
          className="modal-body"
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: matchMD ? 2 : 3,
            "&::-webkit-scrollbar": { width: "8px" },
            "&::-webkit-scrollbar-track": {
              backgroundColor: theme.palette.grey[100],
              borderRadius: "10px"
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.grey[400],
              borderRadius: "10px",
              "&:hover": { backgroundColor: theme.palette.grey[600] }
            }
          }}
        >
          {/* Basic Info */}
          <Box>
            <Typography
              sx={{
                fontSize: matchMD ? "0.85rem" : "0.9rem",
                opacity: 0.7,
                fontWeight: 600,
                mb: 1
              }}
            >
              {t("basic-information", "Basic Information")}
            </Typography>
            <Box display="flex" flexDirection="column" gap={matchMD ? 1.5 : 2}>
              <TextField
                label={t("store-name", "Store Name")}
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                size="small"
              />
              <FormControl fullWidth size="small">
                <InputLabel>{t("status", "Status")}</InputLabel>
                <Select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as StoreStatus)}
                  label={t("status", "Status")}
                >
                  {Object.values(StoreStatus).map((s) => (
                    <MenuItem key={s} value={s}>
                      {t(
                        `status-${s.toLowerCase()}`,
                        s.charAt(0) + s.slice(1).toLowerCase()
                      )}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* Contact Information */}
          <Paper
            variant="outlined"
            sx={{
              p: matchMD ? 1.5 : 2,
              display: "flex",
              flexDirection: "column",
              gap: matchMD ? 1.5 : 2
            }}
          >
            <Typography
              variant={matchMD ? "body1" : "subtitle1"}
              fontWeight={600}
              color="textPrimary"
            >
              {t("contact-information", "Contact Information")}
            </Typography>

            <Box display="flex" flexDirection="column" gap={matchMD ? 1.5 : 2}>
              <TextField
                label={t("email", "Email")}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                size="small"
              />

              {/* Phone Number with Country Code */}
              <Box
                sx={{
                  display: "flex",
                  gap: matchMD ? 1 : 2,
                  alignItems: "flex-start",
                  flexDirection: matchMD ? "column" : "row"
                }}
              >
                <FormControl
                  sx={{
                    flex: matchMD ? 1 : "0 0 160px",
                    minWidth: matchMD ? "100%" : 140,
                    width: matchMD ? "100%" : "auto"
                  }}
                  variant="outlined"
                  error={!!phoneError}
                  disabled={isLoading}
                  size="small"
                >
                  <InputLabel id="phone-country-code-label">
                    {t("country-code", "Code")}
                  </InputLabel>
                  <Select
                    value={phoneCountryCode || "+250"}
                    onChange={(e) =>
                      handlePhoneCountryCodeChange(e.target.value)
                    }
                    labelId="phone-country-code-label"
                    id="phone-country-code"
                    label={t("country-code", "Code")}
                    disabled={isLoading}
                  >
                    {Object.keys(PhoneCountryCodeMap).map((key) => (
                      <MenuItem key={key} value={key}>
                        {`${PhoneCountryCodeMap[key].flag} ${PhoneCountryCodeMap[key].code}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label={t("phone-number", "Phone Number")}
                  value={phoneNumber}
                  onChange={(e) => handlePhoneNumberChange(e.target.value)}
                  fullWidth
                  size="small"
                  error={!!phoneError}
                  helperText={phoneError}
                  placeholder="782484464"
                  disabled={isLoading}
                />
              </Box>
            </Box>
          </Paper>

          {/* Address */}
          <Paper
            variant="outlined"
            sx={{
              p: matchMD ? 1.5 : 2,
              display: "flex",
              flexDirection: "column",
              gap: matchMD ? 1.5 : 2
            }}
          >
            <Typography
              variant={matchMD ? "body1" : "subtitle1"}
              fontWeight={600}
              color="textPrimary"
            >
              {t("store-address", "Store Address")}
            </Typography>

            <Box display="flex" flexDirection="column" gap={matchMD ? 1.5 : 2}>
              <FormControl fullWidth size="small" disabled>
                <InputLabel>{t("country", "Country")}</InputLabel>
                <Select value={country} label={t("country", "Country")}>
                  {Object.values(CountryFullName).map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>{t("province", "Province")}</InputLabel>
                <Select
                  value={province}
                  onChange={(e) => handleProvinceChange(e.target.value)}
                  disabled={locationLoading}
                  label={t("province", "Province")}
                  MenuProps={{ container: document.body }}
                >
                  {provinces.map((p) => (
                    <MenuItem key={p} value={p}>
                      {t(`location-province-${p}`, p)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {province && (
                <FormControl fullWidth size="small">
                  <InputLabel>{t("district", "District")}</InputLabel>
                  <Select
                    value={district}
                    onChange={(e) => handleDistrictChange(e.target.value)}
                    label={t("district", "District")}
                    MenuProps={{ container: document.body }}
                  >
                    {districts.map((d) => (
                      <MenuItem key={d} value={d}>
                        {t(`location-district-${d}`, d)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {district && (
                <FormControl fullWidth size="small">
                  <InputLabel>{t("sector", "Sector")}</InputLabel>
                  <Select
                    value={sector}
                    onChange={(e) => handleSectorChange(e.target.value)}
                    label={t("sector", "Sector")}
                    MenuProps={{ container: document.body }}
                  >
                    {sectors.map((s) => (
                      <MenuItem key={s} value={s}>
                        {t(`location-sector-${s}`, s)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {sector && (
                <FormControl fullWidth size="small">
                  <InputLabel>{t("cell", "Cell")}</InputLabel>
                  <Select
                    value={cell}
                    onChange={(e) => handleCellChange(e.target.value)}
                    label={t("cell", "Cell")}
                    MenuProps={{ container: document.body }}
                  >
                    {cells.map((c) => (
                      <MenuItem key={c} value={c}>
                        {t(`location-cell-${c}`, c)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {cell && (
                <FormControl fullWidth size="small">
                  <InputLabel>{t("village", "Village")}</InputLabel>
                  <Select
                    value={village}
                    onChange={(e) => setVillage(e.target.value)}
                    label={t("village", "Village")}
                    MenuProps={{ container: document.body }}
                  >
                    {villages.map((v) => (
                      <MenuItem key={v} value={v}>
                        {t(`location-village-${v}`, v)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {village && (
                <TextField
                  label={t("street-address", "Street Address")}
                  value={streetAddress}
                  onChange={(e) => setStreetAddress(e.target.value)}
                  fullWidth
                  size="small"
                />
              )}
            </Box>
          </Paper>
        </Box>

        {/* ── modal-footer: fixed bottom, flex-shrink:0 ── */}
        <Box className="modal-footer">
          <LoadingButton
            onClick={handleUpdate}
            loading={isLoading}
            disabled={!isFormDirty || !!phoneError}
            variant="contained"
            sx={{
              flex: 1,
              textTransform: "none",
              borderRadius: 2,
              backgroundColor: BRAND_COLOR,
              "&:hover": { backgroundColor: BRAND_HOVER }
            }}
          >
            Update Store
          </LoadingButton>
          <Button
            onClick={onClose}
            variant="outlined"
            disabled={isLoading}
            sx={{
              flex: 1,
              textTransform: "none",
              borderRadius: 2,
              backgroundColor: "white",
              color: BRAND_COLOR,
              border: `1px solid ${BRAND_COLOR}`,
              "&:hover": { backgroundColor: BRAND_HOVER, color: "white" }
            }}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default UpdateStoreModal;
